import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { api, errCode } from "@/lib/api";
import { PageHead, Table } from "@/pages/admin/AdminApp";
import SecurityPanel, { useStepUp } from "@/components/SecurityPanel";
import { Tone } from "@/components/store/StatusBadge";

const SEV = { red: "red", yellow: "yellow", green: "green" };

export function Alerts() {
  const { t, date } = useI18n();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/alerts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-alerts"><PageHead title={t("admin.nav_alerts")} />
      <div className="space-y-2">{rows.length === 0 && <div className="card p-10 text-center muted">{t("common.empty")}</div>}
        {rows.map((a) => <Link key={a.alert_id} to={a.link} onClick={() => api.post(`/admin/alerts/${a.alert_id}/read`)} data-testid={`alert-${a.alert_id}`} className={`card p-4 flex items-center gap-3 hover:border-[#2563EB] ${a.read ? "opacity-60" : ""}`}>
          <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${a.severity === "red" ? "bg-[#DC2626]" : a.severity === "green" ? "bg-[#16A34A]" : "bg-amber-500"}`} />
          <div className="flex-1 min-w-0"><p className="text-sm font-medium">{t(`alert.${a.kind}`, Object.fromEntries(Object.entries(a.params || {}).map(([k, v]) => [k, Array.isArray(v) ? v.join(", ") : String(v)])))}</p><p className="text-xs muted">{t(`alert_cat.${a.category}`)} · {date(a.created_at)}</p></div>
          <Tone tone={SEV[a.severity]}>{t(`severity.${a.severity}`)}</Tone></Link>)}</div>
    </div>
  );
}

function FieldsFor({ data, onChange }) {
  const { t } = useI18n();
  return rows(data, onChange, "", t);
}

function rows(data, onChange, prefix, t) {
  return <div className="grid sm:grid-cols-2 gap-4">{Object.entries(data).map(([k, v]) => {
    const id = `${prefix}${k}`; const label = t(`settings.k_${k}`);
    const set = (val) => onChange({ ...data, [k]: val });
    if (v && typeof v === "object" && "configured" in v) return <label key={id}><span className="label">{label} <span className={v.configured ? "text-[#16A34A]" : "muted"}>({t(v.configured ? "settings.configured" : "settings.not_configured")})</span></span><input data-testid={`set-${id}`} type="password" autoComplete="off" dir="ltr" className="input" placeholder="••••••" onChange={(e) => set(e.target.value || { configured: v.configured })} /></label>;
    if (v && typeof v === "object" && "ar" in v) return <div key={id} className="sm:col-span-2 grid sm:grid-cols-2 gap-3">{["ar", "en"].map((l) => <label key={l}><span className="label">{label} ({l.toUpperCase()})</span><textarea dir={l === "ar" ? "rtl" : "ltr"} className="textarea min-h-[70px]" value={v[l]} onChange={(e) => set({ ...v, [l]: e.target.value })} /></label>)}</div>;
    if (v && typeof v === "object" && !Array.isArray(v)) return <fieldset key={id} className="sm:col-span-2 rounded-xl border p-4 space-y-3"><legend className="px-2 text-sm font-semibold">{label}</legend>{rows(v, set, `${id}.`, t)}</fieldset>;
    if (typeof v === "boolean") return <label key={id} className="flex items-center gap-3 text-sm"><input data-testid={`set-${id}`} type="checkbox" className="w-5 h-5 accent-[#2563EB]" checked={v} onChange={(e) => set(e.target.checked)} />{label}</label>;
    if (Array.isArray(v)) return <label key={id}><span className="label">{label}</span><input className="input" dir="ltr" value={v.join(",")} onChange={(e) => set(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></label>;
    if (["mode", "provider", "window_start", "automation_level"].includes(k)) {
      const choices = { mode: data.provider !== undefined ? ["test", "live"] : ["manual", "auto"], provider: ["moyasar", "tap"], window_start: ["delivery", "order"], automation_level: ["advisory", "assist"] }[k];
      return <label key={id}><span className="label">{label}</span><select data-testid={`set-${id}`} className="input" value={v} onChange={(e) => set(e.target.value)}>{choices.map((c) => <option key={c} value={c}>{t(`settings.opt_${c}`)}</option>)}</select></label>;
    }
    return <label key={id}><span className="label">{label}</span><input data-testid={`set-${id}`} type={typeof v === "number" ? "number" : "text"} step="any" className="input" value={v ?? ""} onChange={(e) => set(typeof v === "number" ? Number(e.target.value) : e.target.value)} /></label>;
  })}</div>;
}

const SECTIONS = ["general", "orders", "fulfillment", "pricing", "shipping", "tax", "returns", "refunds", "payments", "suppliers", "ai", "wallet", "security", "channels"];
const MONEY_NOTE = ["shipping", "orders", "refunds", "wallet", "ai"];

export function SettingsPage() {
  const { t } = useI18n();
  const [s, setS] = useState(null);
  const [sec, setSec] = useState("general");
  const [draft, setDraft] = useState(null);
  const { guard, dialog } = useStepUp();
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  useEffect(() => { if (s) setDraft(structuredClone(s[sec])); }, [s, sec]);
  const strip = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => !(v && typeof v === "object" && "configured" in v)).map(([k, v]) => [k, v && typeof v === "object" && !Array.isArray(v) && !("ar" in v) ? strip(v) : v]));
  const save = guard(async () => { const { data } = await api.put(`/admin/settings/${sec}`, strip(draft)); setS({ ...s, ...data }); toast.success(t("common.saved")); });
  if (!s || !draft) return null;
  return (
    <div data-testid="admin-settings">
      {dialog}
      <PageHead title={t("admin.nav_settings")} sub={t("settings.sub")} />
      <div className="flex gap-2 overflow-x-auto scrollbar-none mb-5">{SECTIONS.map((x) => <button key={x} data-testid={`settings-tab-${x}`} onClick={() => setSec(x)} className={`btn-sm btn shrink-0 ${sec === x ? "bg-[#0B0D0F] text-white" : "bg-white border"}`}>{t(`settings.s_${x}`)}</button>)}</div>
      <section className="card p-5 sm:p-6 space-y-5">
        <p className="text-sm muted">{t(`settings.d_${sec}`)}</p>
        {MONEY_NOTE.includes(sec) && <p className="text-xs muted">{t("settings.halalas_note")}</p>}
        <FieldsFor data={draft} onChange={setDraft} />
        {sec === "channels" && <p className="text-xs muted">{t("settings.channels_note")}</p>}
        <button data-testid="settings-save-btn" onClick={() => save().catch((e) => toast.error(t(`errors.${errCode(e)}`)))} className="btn-primary">{t("common.save")}</button>
      </section>
    </div>
  );
}

export function SecurityPage() {
  const { t, date } = useI18n();
  const [audit, setAudit] = useState({ items: [] });
  useEffect(() => { api.get("/admin/audit").then((r) => setAudit(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="admin-security">
      <PageHead title={t("admin.nav_security")} sub={t("security.admin_sub")} />
      <SecurityPanel staff />
      <h2 className="font-semibold">{t("security.audit")}</h2>
      <Table testid="audit-table" rows={audit.items} cols={[[t("admin.date"), (a) => date(a.created_at)], [t("security.event"), (a) => a.event_type], [t("security.result"), (a) => <Tone tone={a.result === "success" ? "green" : "red"}>{a.result}</Tone>], [t("finance.actor"), (a) => a.actor], [t("security.entity"), (a) => `${a.entity || ""} ${a.entity_id ? a.entity_id.slice(-10) : ""}`], ["IP", (a) => a.metadata?.ip || "—"]]} />
    </div>
  );
}

export function Staff() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [f, setF] = useState({ email: "", name: "", role: "support", password: "" });
  const { guard, dialog } = useStepUp();
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const add = guard(async () => { await api.post("/admin/staff", f); toast.success(t("common.saved")); load(); });
  const roles = ["admin", "support", "order_manager", "finance", "viewer"];
  return (
    <div className="space-y-6" data-testid="admin-staff">{dialog}
      <PageHead title={t("admin.nav_staff")} sub={t("staff.sub")} />
      <Table rows={rows} cols={[[t("checkout.name"), (u) => u.name], [t("checkout.email"), (u) => u.email], [t("staff.role"), (u) => t(`roles.${u.role}`)]]} />
      <section className="card p-5 grid sm:grid-cols-5 gap-2 items-end">
        <input data-testid="staff-email" className="input h-10" placeholder={t("checkout.email")} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <input className="input h-10" placeholder={t("checkout.name")} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <select data-testid="staff-role" className="input h-10" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>{roles.map((r) => <option key={r} value={r}>{t(`roles.${r}`)}</option>)}</select>
        <input type="password" className="input h-10" placeholder={t("staff.temp_password")} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
        <button data-testid="staff-add-btn" onClick={() => add().catch((e) => toast.error(t(`errors.${errCode(e)}`)))} className="btn-dark btn-sm h-10">{t("common.add")}</button>
      </section>
    </div>
  );
}

export function SupportInbox() {
  const { t, date } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/support").then((r) => setRows(r.data)); }, []);
  return (
    <div data-testid="admin-support"><PageHead title={t("admin.nav_support")} />
      <div className="space-y-3">{rows.length === 0 && <div className="card p-10 text-center muted">{t("common.empty")}</div>}
        {rows.map((m) => <div key={m.message_id} className="card p-5 space-y-1"><div className="flex justify-between"><p className="font-semibold">{m.subject}</p><span className="text-xs muted">{date(m.created_at)}</span></div><p className="text-xs muted">{m.name} · {m.email} {m.order_number && `· ${m.order_number}`}</p><p className="text-sm whitespace-pre-line pt-2">{m.message}</p></div>)}</div>
    </div>
  );
}
